import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Container,
  CardContent,
  Grid,
  Divider as MuiDivider,
  Badge,
  Menu,
  MenuItem as MuiMenuItem,
  Divider,
  Button,
  Rating,
  useTheme,
  useMediaQuery,
  Collapse,
  Chip,
  Avatar,
} from '@mui/material';
import {
  LayoutDashboard,
  GraduationCap,
  ClipboardList,
  Users,
  UserCheck,
  Megaphone,
  FileEdit,
  ClipboardCheck,
  BarChart3,
  LogOut,
  User,
  Settings,
  History,
  Bell,
  MessageSquare,
  Award,
  Trash2,
  BookOpen,
  Calendar,
  FolderOpen,
  MessageSquarePlus,
  FileSpreadsheet,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Activity,
  Library,
  Globe,
  Building2,
  FileQuestion,
  Layers,
} from 'lucide-react';
import { useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';
import { GitHubBrandIcon } from '../components/BrandIcons';

import PlacementManager from './PlacementManager';
import AdminDashboard from './AdminDashboard';
import SupervisorStudents from './SupervisorStudents';
import MonitoringDashboard from './MonitoringDashboard';
import SchoolManager from './SchoolManager';
import CourseManager from './CourseManager';
import StudentManager from './StudentManager';
import SupervisorManager from './SupervisorManager';
import AnnouncementManager from './AnnouncementManager';
import EventManager from './EventManager';
import RegistryReport from './RegistryReport';
import SystemSettings from './SystemSettings';
import AuditLogs from './AuditLogs';
import RecycleBin from './RecycleBin';
import MyEvaluations from './MyEvaluations';
import Portfolio from './Portfolio';
import GuidanceChat from './GuidanceChat';
import Profile from './Profile';
import ResourceCenter from './ResourceCenter';
import SchoolEvaluation from './SchoolEvaluation';
import SchoolEvaluationReport from './SchoolEvaluationReport';
import EvaluationFormManager from './EvaluationFormManager';
import WorkloadFormManager from './WorkloadFormManager';
import SupervisorWorkloadEvaluation from './SupervisorWorkloadEvaluation';
import StudentWorkloadSubmission from './StudentWorkloadSubmission';
import SystemUserManager from './SystemUserManager';
import SupervisionReport from './SupervisionReport';
import StudentDemandSurvey from './StudentDemandSurvey';
import ClassroomResearchDatabase from './ClassroomResearchDatabase';
import ComprehensiveExamManager from './ComprehensiveExamManager';
import StudentExamPortal from './StudentExamPortal';
import LandingPageManager from './LandingPageManager';
import { WorkloadDraftAlertModal } from '../components/WorkloadDraftAlertModal';
import { APP_VERSION } from '../version';

const drawerWidth = 256;

const getEventColor = (type: string) => {
  switch (type) {
    case 'DEADLINE':
      return '#DC2626';
    case 'MEETING':
      return '#1E40AF';
    default:
      return '#64748B';
  }
};

const getRoleLabel = (role?: string) => {
  switch (role) {
    case 'ADMIN':
      return 'ผู้ดูแลระบบ';
    case 'STAFF':
      return 'เจ้าหน้าที่ฝ่ายฝึกฯ';
    case 'RESEARCH_STAFF':
      return 'เจ้าหน้าที่ฝ่ายวิจัย';
    case 'DEAN':
      return 'ผู้บริหารคณะ';
    case 'SUPERVISOR':
      return 'อาจารย์นิเทศก์';
    case 'CO_TEACHER':
      return 'ครูพี่เลี้ยง';
    case 'STUDENT':
      return 'นักศึกษาฝึกสอน';
    default:
      return role || 'ผู้ใช้งาน';
  }
};

const HomeView = ({ profile }: { profile: any }) => {
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState<any>(null);
  const [evalSummary, setEvalSummary] = useState<any>(null);
  const [workloadSummary, setWorkloadSummary] = useState<any>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const featRes = await api.get('/placements/featured');
        setFeatured(featRes.data);

        const eventRes = await api.get('/events/upcoming');
        setEvents(eventRes.data);

        if (user.role === 'STUDENT') {
          try {
            const pRes = await api.get('/placements/my-placement');
            const placementData = pRes.data;
            setSummary(placementData);

            if (placementData && placementData.id) {
              const evalRes = await api.get(`/evaluations/summary/${placementData.id}`);
              setEvalSummary(evalRes.data);
            }
          } catch (e) {
            console.error('No placement error:', e);
          }

          try {
            const wRes = await api.get('/workload-evaluations/my-tasks');
            setWorkloadSummary(wRes.data);
          } catch (we) {
            console.error('No workload tasks error:', we);
          }
        } else if (user.role === 'SUPERVISOR' || user.role === 'CO_TEACHER') {
          const sRes = await api.get('/evaluations/assigned-students');
          setSummary({ studentCount: sRes.data.length, students: sRes.data });
        }
      } catch (err) {
        console.error('Dashboard main fetch error:', err);
      }
    };
    fetchData();
  }, [user.role, user.id]);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PASSED':
        return 'ผ่านเกณฑ์ (PASSED)';
      case 'FAILED':
        return 'ไม่ผ่านเกณฑ์ (FAILED)';
      default:
        return 'รอดำเนินการ (PENDING)';
    }
  };

  return (
    <>
      {['ADMIN', 'STAFF', 'DEAN', 'SUPERVISOR'].includes(user.role) ? (
        <>
          <Box sx={{ mb: 5, mt: 1 }}>
            <AdminDashboard
              isSupervisorView={user.role === 'SUPERVISOR'}
              supervisorMajor={profile?.department}
            />
          </Box>

          {featured.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(217, 119, 6, 0.1)', color: '#D97706', width: 40, height: 40 }}>
                  <Award size={22} />
                </Avatar>
                <Box>
                  <Typography variant="overline" sx={{ color: '#D97706', fontWeight: 800, letterSpacing: '0.08em', display: 'block', lineHeight: 1 }}>
                    STUDENT EXCELLENCE & SHOWCASE
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: '"Prompt", sans-serif', color: '#0F172A' }}>
                    ผลงานดีเด่น (Wall of Fame)
                  </Typography>
                </Box>
              </Box>
              <MuiDivider sx={{ mb: 3 }} />
              <Grid container spacing={2.5}>
                {featured.map((f: any) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.id}>
                    <GlassCard
                      highlightColor="#D97706"
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(0,0,0,0.09)' },
                      }}
                      onClick={() => navigate(`/dashboard/portfolio/${f.id}`)}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 700 }}>
                          {f.first_name} {f.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {f.major}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#475569' }}>
                          {f.school_name}
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                          <Rating value={f.avg_score / 20} readOnly size="small" />
                          <Typography variant="caption" sx={{ ml: 1, fontWeight: 600 }}>
                            ({parseFloat(f.avg_score).toFixed(0)}%)
                          </Typography>
                        </Box>
                      </CardContent>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      ) : (
        <>
          {user.role === 'STUDENT' ? (
            <GlassCard highlightColor="#1E40AF" sx={{ mb: 4 }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(30, 64, 175, 0.1)', color: 'primary.main', width: 40, height: 40 }}>
                    <GraduationCap size={22} />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 700, lineHeight: 1 }}>
                      STUDENT PRACTICUM OVERVIEW
                    </Typography>
                    <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700 }}>
                      ข้อมูลการฝึกประสบการณ์และผลการประเมิน
                    </Typography>
                  </Box>
                </Box>

                {summary ? (
                  <>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          โรงเรียนฝึกประสบการณ์:
                        </Typography>
                        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 700 }}>
                          {summary.school_name || '-'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          รายวิชาปฏิบัติงาน:
                        </Typography>
                        <Typography variant="body1" color="text.primary" sx={{ fontWeight: 700 }}>
                          {summary.course_name ? `${summary.course_code} ${summary.course_name}` : '-'}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2.5 }} />

                    {evalSummary || workloadSummary ? (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
                          สถานะการได้รับการนิเทศและประเมินผล
                        </Typography>
                        <Grid container spacing={2}>
                          {Number(summary?.requires_supervision ?? 1) !== 0 ? (
                            <>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    bgcolor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    วิชาชีพครู (Professional)
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 700,
                                      color:
                                        evalSummary?.prof_evaluated || (evalSummary?.prof_score !== null && evalSummary?.prof_score !== undefined)
                                          ? 'success.main'
                                          : 'warning.main',
                                      mt: 0.8,
                                    }}
                                  >
                                    {evalSummary?.prof_evaluated || (evalSummary?.prof_score !== null && evalSummary?.prof_score !== undefined)
                                      ? 'ได้รับการนิเทศแล้ว'
                                      : 'รอดำเนินการ'}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    bgcolor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    วิชาเอก (Specialist)
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 700,
                                      color:
                                        evalSummary?.spec_evaluated || (evalSummary?.spec_score !== null && evalSummary?.spec_score !== undefined)
                                          ? 'success.main'
                                          : 'warning.main',
                                      mt: 0.8,
                                    }}
                                  >
                                    {evalSummary?.spec_evaluated || (evalSummary?.spec_score !== null && evalSummary?.spec_score !== undefined)
                                      ? 'ได้รับการนิเทศแล้ว'
                                      : 'รอดำเนินการ'}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    bgcolor: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                    ครูพี่เลี้ยง (Mentor)
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 700,
                                      color:
                                        evalSummary?.co_evaluated || (evalSummary?.co_score !== null && evalSummary?.co_score !== undefined)
                                          ? 'success.main'
                                          : 'warning.main',
                                      mt: 0.8,
                                    }}
                                  >
                                    {evalSummary?.co_evaluated || (evalSummary?.co_score !== null && evalSummary?.co_score !== undefined)
                                      ? 'ได้รับการนิเทศแล้ว'
                                      : 'รอดำเนินการ'}
                                  </Typography>
                                </Box>
                              </Grid>
                            </>
                          ) : (
                            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 3,
                                  bgcolor: '#F8FAFC',
                                  border: '1px solid #E2E8F0',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Box>
                                  <Chip label="ยกเว้นการนิเทศ" size="small" variant="outlined" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.8 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    รายวิชานี้ได้รับการกำหนดให้<strong>ยกเว้นการนิเทศ</strong> ไม่ต้องรับการประเมินจากอาจารย์นิเทศก์และครูพี่เลี้ยง
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          )}
                          <Grid size={{ xs: 12, sm: 6, md: Number(summary?.requires_supervision ?? 1) === 0 ? 6 : 3 }}>
                            <Box
                              sx={{
                                p: 2,
                                textAlign: 'center',
                                borderRadius: 3,
                                bgcolor: '#F8FAFC',
                                border: '1px solid #E2E8F0',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                ภาระงาน (Workload)
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 700,
                                  color:
                                    workloadSummary?.is_evaluated || workloadSummary?.evaluation_status === 'ประเมินแล้ว' || (workloadSummary?.total_score !== null && workloadSummary?.total_score !== undefined)
                                      ? 'success.main'
                                      : 'warning.main',
                                  mt: 0.8,
                                }}
                              >
                                {workloadSummary?.is_evaluated || workloadSummary?.evaluation_status === 'ประเมินแล้ว' || (workloadSummary?.total_score !== null && workloadSummary?.total_score !== undefined)
                                  ? 'ได้รับการประเมินแล้ว'
                                  : 'รอดำเนินการ'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {evalSummary && (
                          <Box
                            sx={{
                              mt: 2.5,
                              p: 1.8,
                              borderRadius: 3,
                              textAlign: 'center',
                              bgcolor:
                                evalSummary.status === 'PASSED'
                                  ? '#ECFDF5'
                                  : evalSummary.status === 'FAILED'
                                  ? '#FEF2F2'
                                  : '#FFFBEB',
                              color:
                                evalSummary.status === 'PASSED'
                                  ? '#065F46'
                                  : evalSummary.status === 'FAILED'
                                  ? '#991B1B'
                                  : '#92400E',
                              border: '1px solid',
                              borderColor:
                                evalSummary.status === 'PASSED'
                                  ? '#A7F3D0'
                                  : evalSummary.status === 'FAILED'
                                  ? '#FECACA'
                                  : '#FDE68A',
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              สถานะการฝึกประสบการณ์วิชาชีพครู: {getStatusLabel(evalSummary.status)}
                            </Typography>
                            {evalSummary.remark && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.5,
                                  fontWeight: 600,
                                  color: evalSummary.remark.includes('ไม่ผ่าน') ? '#B91C1C' : '#B45309',
                                }}
                              >
                                (หมายเหตุ: {evalSummary.remark})
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileEdit size={16} />}
                            onClick={() => navigate('/dashboard/workload-submission')}
                          >
                            ส่งภาระงานตามรายวิชา
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Award size={16} />}
                            onClick={() => navigate('/dashboard/portfolio')}
                          >
                            ดูแฟ้มสะสมผลงาน (e-Portfolio)
                          </Button>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<Library size={16} />}
                            onClick={() => navigate('/dashboard/research')}
                          >
                            ฐานข้อมูลงานวิจัยในชั้นเรียน
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 2.5,
                          bgcolor: '#F8FAFC',
                          borderRadius: 2.5,
                          border: '1px dashed #CBD5E1',
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          * ยังไม่ได้รับการประเมินจากคณะกรรมการ...
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" gutterBottom color="text.secondary">
                      ไม่พบข้อมูลการจัดสรรที่ฝึกประสบการณ์
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      กรุณาติดต่อฝ่ายฝึกประสบการณ์ฯ เพื่อขอรับการจัดสรร
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </GlassCard>
          ) : (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                {user.role === 'CO_TEACHER' && summary && (
                  <GlassCard highlightColor="#059669" sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', py: 5 }}>
                      <Typography variant="overline" color="success.main" sx={{ fontWeight: 700 }}>
                        MENTOR OVERVIEW
                      </Typography>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        นักศึกษาฝึกประสบการณ์วิชาชีพครูในความดูแล
                      </Typography>
                      <Typography variant="h2" color="success.main" sx={{ fontWeight: 800 }}>
                        {summary.studentCount}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        คน
                      </Typography>
                    </CardContent>
                  </GlassCard>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <GlassCard highlightColor="#1E40AF" sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Calendar size={20} color="#1E40AF" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      กำหนดการเร็วๆ นี้
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <List sx={{ pt: 0 }}>
                    {events.map((e: any) => (
                      <ListItem
                        key={e.id}
                        disablePadding
                        sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box
                            sx={{
                              width: 4,
                              height: 30,
                              bgcolor: getEventColor(e.event_type),
                              mr: 1.5,
                              borderRadius: 1,
                            }}
                          />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {e.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(e.event_date).toLocaleDateString('th-TH', {
                                day: 'numeric',
                                month: 'short',
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                    {events.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        ไม่มีกำหนดการเร็วๆ นี้
                      </Typography>
                    )}
                  </List>
                </GlassCard>
              </Grid>
            </Grid>
          )}

          {featured.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Avatar sx={{ bgcolor: 'rgba(217, 119, 6, 0.1)', color: '#D97706', width: 40, height: 40 }}>
                  <Award size={22} />
                </Avatar>
                <Box>
                  <Typography variant="overline" sx={{ color: '#D97706', fontWeight: 800, letterSpacing: '0.08em', display: 'block', lineHeight: 1 }}>
                    STUDENT EXCELLENCE & SHOWCASE
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, fontFamily: '"Prompt", sans-serif', color: '#0F172A' }}>
                    ผลงานดีเด่น (Wall of Fame)
                  </Typography>
                </Box>
              </Box>
              <MuiDivider sx={{ mb: 3 }} />
              <Grid container spacing={2.5}>
                {featured.map((f: any) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.id}>
                    <GlassCard
                      highlightColor="#D97706"
                      sx={{
                        height: '100%',
                        cursor: user.role === 'CO_TEACHER' ? 'default' : 'pointer',
                        '&:hover':
                          user.role === 'CO_TEACHER'
                            ? {}
                            : { transform: 'translateY(-4px)', boxShadow: '0 12px 28px rgba(0,0,0,0.09)' },
                      }}
                      onClick={() => {
                        if (user.role !== 'CO_TEACHER') {
                          navigate(`/dashboard/portfolio/${f.id}`);
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 700 }}>
                          {f.first_name} {f.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {f.major}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                          {f.school_name}
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                          <Rating value={f.avg_score / 20} readOnly size="small" />
                          <Typography variant="caption" sx={{ ml: 1, fontWeight: 600 }}>
                            ({parseFloat(f.avg_score).toFixed(0)}%)
                          </Typography>
                        </Box>
                      </CardContent>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}
    </>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const handleMenuClick = (menuKey: string) => {
    setOpenMenus((prev) => ({ ...prev, [menuKey]: !prev[menuKey] }));
  };

  const getNavItems = () => {
    let isSurveyOpen = false;
    if (systemSettings && systemSettings.demand_survey_open !== '0') {
      const now = new Date();
      const start = systemSettings.demand_survey_start_date
        ? new Date(systemSettings.demand_survey_start_date)
        : null;
      const end = systemSettings.demand_survey_end_date
        ? new Date(systemSettings.demand_survey_end_date)
        : null;
      if (start && !isNaN(start.getTime()) && now < start) {
        isSurveyOpen = false;
      } else if (end && !isNaN(end.getTime()) && now > end) {
        isSurveyOpen = false;
      } else {
        isSurveyOpen = true;
      }
    }
    const demandRoles = isSurveyOpen ? ['CO_TEACHER', 'ADMIN', 'STAFF'] : ['ADMIN', 'STAFF'];

    const rawMenuItems = [
      {
        text: 'ภาพรวมระบบ',
        icon: <LayoutDashboard size={19} />,
        roles: ['ADMIN', 'SUPERVISOR', 'CO_TEACHER', 'STUDENT', 'DEAN', 'STAFF'],
        path: '/dashboard',
      },
      {
        text: 'รายงานและวิเคราะห์ผล',
        icon: <BarChart3 size={19} />,
        roles: ['ADMIN', 'STAFF', 'DEAN'],
        children: [
          {
            text: 'รายงานฝ่ายทะเบียน',
            icon: <FileSpreadsheet size={17} />,
            path: '/dashboard/registry-report',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'รายงานการนิเทศ',
            icon: <ClipboardCheck size={17} />,
            path: '/dashboard/supervision-report',
            roles: ['ADMIN', 'STAFF', 'DEAN'],
          },
          {
            text: 'รายงานประเมินสถานศึกษา',
            icon: <Building2 size={17} />,
            path: '/dashboard/school-eval-report',
            roles: ['ADMIN', 'STAFF'],
          },
        ],
      },
      {
        text: 'จัดการข้อมูลพื้นฐาน',
        icon: <Users size={19} />,
        roles: ['ADMIN', 'STAFF'],
        children: [
          {
            text: 'จัดการรายวิชา',
            icon: <BookOpen size={17} />,
            path: '/dashboard/courses',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'จัดการนักศึกษา',
            icon: <Users size={17} />,
            path: '/dashboard/students',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'จัดการอาจารย์นิเทศก์',
            icon: <UserCheck size={17} />,
            path: '/dashboard/supervisors',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'จัดการโรงเรียนและครูพี่เลี้ยง',
            icon: <Building2 size={17} />,
            path: '/dashboard/schools',
            roles: ['ADMIN', 'STAFF'],
          },
        ],
      },
      {
        text: 'การจัดสรรและเตรียมการฝึกสอน',
        icon: <ClipboardList size={19} />,
        roles: ['ADMIN', 'STAFF', 'CO_TEACHER'],
        children: [
          {
            text: 'สำรวจความต้องการนักศึกษา',
            icon: <Users size={17} />,
            path: '/dashboard/demand-survey',
            roles: demandRoles,
          },
          {
            text: 'การจัดสรรรายวิชาและโรงเรียนฝึกประสบการณ์',
            icon: <ClipboardList size={17} />,
            path: '/dashboard/placements',
            roles: ['ADMIN', 'STAFF'],
          },
        ],
      },
      {
        text: 'กิจกรรมการฝึกปฏิบัติงาน',
        icon: <GraduationCap size={19} />,
        roles: ['ADMIN', 'STAFF', 'STUDENT', 'SUPERVISOR', 'CO_TEACHER', 'DEAN', 'RESEARCH_STAFF'],
        children: [
          {
            text: 'ส่งภาระงานตามรายวิชา',
            icon: <FileEdit size={17} />,
            path: '/dashboard/workload-submission',
            roles: ['STUDENT'],
          },
          {
            text: 'แฟ้มสะสมผลงาน (e-Portfolio)',
            icon: <Award size={17} />,
            path: '/dashboard/portfolio',
            roles: ['STUDENT', 'SUPERVISOR', 'ADMIN', 'STAFF', 'DEAN'],
          },
          {
            text: 'ปรึกษา/กล่องข้อความ',
            icon: <MessageSquare size={17} />,
            path: '/dashboard/chat',
            roles: ['SUPERVISOR', 'CO_TEACHER', 'STUDENT'],
          },
          {
            text: 'ฐานข้อมูลงานวิจัยในชั้นเรียน',
            icon: <Library size={17} />,
            path: '/dashboard/research',
            roles: ['ADMIN', 'STAFF', 'DEAN', 'SUPERVISOR', 'CO_TEACHER', 'STUDENT', 'RESEARCH_STAFF'],
          },
        ],
      },
      {
        text: 'การนิเทศ การสอบ และประเมินผล',
        icon: <ClipboardCheck size={19} />,
        roles: ['ADMIN', 'STAFF', 'SUPERVISOR', 'CO_TEACHER', 'STUDENT', 'DEAN'],
        children: [
          {
            text: 'การนิเทศ/ประเมินสมรรถนะ',
            icon: <ClipboardCheck size={17} />,
            path: '/dashboard/supervise',
            roles: ['ADMIN', 'STAFF', 'SUPERVISOR', 'CO_TEACHER'],
          },
          {
            text: 'การกรอกคะแนนภาระงานนักศึกษา',
            icon: <ClipboardList size={17} />,
            path: '/dashboard/workload-evaluation',
            roles: ['ADMIN', 'STAFF', 'SUPERVISOR'],
          },
          {
            text: 'ระบบสอบประมวลผลความรู้',
            icon: <FileQuestion size={17} />,
            path: '/dashboard/exams-manage',
            roles: ['ADMIN', 'STAFF', 'SUPERVISOR', 'DEAN'],
          },
          {
            text: 'การสอบประมวลผลความรู้',
            icon: <FileQuestion size={17} />,
            path: '/dashboard/exams',
            roles: ['STUDENT'],
          },
          {
            text: 'จัดการแบบประเมินสมรรถนะ',
            icon: <MessageSquarePlus size={17} />,
            path: '/dashboard/evaluation-forms',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'จัดการแบบประเมินภาระงาน',
            icon: <MessageSquarePlus size={17} />,
            path: '/dashboard/workload-forms',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'ประเมินผลของฉัน',
            icon: <ClipboardCheck size={17} />,
            path: '/dashboard/evaluations',
            roles: ['STUDENT'],
          },
          {
            text: 'ประเมินโรงเรียนฝึกสอน',
            icon: <MessageSquarePlus size={17} />,
            path: '/dashboard/school-feedback',
            roles: ['STUDENT'],
          },
        ],
      },
      {
        text: 'ประชาสัมพันธ์และสื่อ',
        icon: <Megaphone size={19} />,
        roles: ['ADMIN', 'STAFF', 'SUPERVISOR', 'CO_TEACHER', 'STUDENT'],
        children: [
          {
            text: 'ประกาศข่าวสาร',
            icon: <Megaphone size={17} />,
            path: '/dashboard/announcements',
            roles: ['ADMIN', 'STAFF', 'SUPERVISOR', 'CO_TEACHER', 'STUDENT'],
          },
          {
            text: 'จัดการหน้าแรก (Builder)',
            icon: <Layers size={17} />,
            path: '/dashboard/landing-page',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'ปฏิทินกิจกรรม',
            icon: <Calendar size={17} />,
            path: '/dashboard/events',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'คลังสื่อการสอน',
            icon: <FolderOpen size={17} />,
            path: '/dashboard/resources',
            roles: ['ADMIN', 'SUPERVISOR', 'CO_TEACHER', 'STUDENT', 'STAFF'],
          },
        ],
      },
      {
        text: 'การดูแลและตั้งค่าระบบ',
        icon: <Settings size={19} />,
        roles: ['ADMIN', 'STAFF'],
        children: [
          {
            text: 'รายงานสถานะระบบ (System Monitoring)',
            icon: <Activity size={17} />,
            path: '/dashboard/monitoring',
            roles: ['ADMIN'],
          },
          {
            text: 'จัดการผู้ใช้งานระบบ',
            icon: <Users size={17} />,
            path: '/dashboard/system-users',
            roles: ['ADMIN'],
          },
          {
            text: 'บันทึกกิจกรรม',
            icon: <History size={17} />,
            path: '/dashboard/audit',
            roles: ['ADMIN'],
          },
          {
            text: 'ตั้งค่าระบบ',
            icon: <Settings size={17} />,
            path: '/dashboard/settings',
            roles: ['ADMIN', 'STAFF'],
          },
          {
            text: 'ถังขยะ',
            icon: <Trash2 size={17} />,
            path: '/dashboard/recycle-bin',
            roles: ['ADMIN', 'STAFF'],
          },
        ],
      },
    ];

    const filtered: any[] = [];

    rawMenuItems.forEach((item) => {
      if (item.roles.includes(user.role)) {
        if (item.children) {
          const allowedChildren = item.children.filter((child) => child.roles.includes(user.role));
          if (allowedChildren.length > 1) {
            filtered.push({
              ...item,
              children: allowedChildren,
            });
          } else if (allowedChildren.length === 1) {
            filtered.push({
              text: allowedChildren[0].text,
              icon: allowedChildren[0].icon,
              path: allowedChildren[0].path,
              roles: allowedChildren[0].roles,
            });
          }
        } else {
          filtered.push(item);
        }
      }
    });

    return filtered;
  };

  const fetchProfile = async () => {
    try {
      let endpoint = '/auth/me';
      if (user.role === 'STUDENT') endpoint = '/students/profile';
      else if (user.role === 'SUPERVISOR') endpoint = '/supervisors/me';
      else if (user.role === 'CO_TEACHER') endpoint = '/co-teachers/me';

      const res = await api.get(endpoint);
      setProfile(res.data);
    } catch (err) {
      console.error('Failed to fetch profile', err);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEvents = async () => {
    try {
      const eventRes = await api.get('/events/upcoming');
      setEvents(eventRes.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSystemSettings(res.data || {});
    } catch (err) {
      console.error('Failed to fetch settings', err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !user?.id) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`, { replace: true });
      return;
    }
    fetchProfile();
    fetchNotifications();
    fetchEvents();
    fetchSettings();

    // Smart polling: throttle and pause polling when the browser tab is hidden/minimized
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications();
      }
    }, 60000);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.role]);

  useEffect(() => {
    const sendHeartbeat = () => {
      if (document.visibilityState === 'visible') {
        api.post('/monitoring/heartbeat', { currentPath: location.pathname }).catch(() => {});
      }
    };
    sendHeartbeat();
    const hbInterval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(hbInterval);
  }, [location.pathname]);

  useEffect(() => {
    if (user.role === 'RESEARCH_STAFF') {
      if (location.pathname !== '/dashboard/research' && location.pathname !== '/dashboard/profile') {
        navigate('/dashboard/research', { replace: true });
        return;
      }
    }
    const navItems = getNavItems();
    const newOpenMenus: Record<string, boolean> = {};
    navItems.forEach((item: any) => {
      if (item.children) {
        const hasActiveChild = item.children.some((child: any) => location.pathname === child.path);
        if (hasActiveChild) {
          newOpenMenus[item.text] = true;
        }
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...newOpenMenus }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.put(`/notifications/${id}/read`);
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = getNavItems();

  const drawerContent = (
    <Box sx={{ overflow: 'auto', pb: 4, height: '100%', bgcolor: '#FFFFFF' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, minHeight: 70 }}>
        <Box
          onClick={() => {
            navigate('/dashboard');
            if (mobileOpen) setMobileOpen(false);
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', flexGrow: 1 }}
        >
          <Avatar
            src="/logo.png"
            alt="TPMS Logo"
            sx={{ width: 38, height: 38, p: 0.3, bgcolor: 'primary.main', boxShadow: '0 3px 8px rgba(30,64,175,0.2)' }}
          />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1E3A8A', lineHeight: 1 }}>
                TPMS
              </Typography>
              <Chip
                label="KPRU"
                size="small"
                sx={{
                  bgcolor: 'rgba(217, 119, 6, 0.12)',
                  color: '#B45309',
                  fontWeight: 700,
                  height: 18,
                  fontSize: '0.65rem',
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.7rem' }}>
              ครุศาสตร์ มรภ.กำแพงเพชร
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => setDesktopOpen(false)}
          sx={{ display: { xs: 'none', md: 'inline-flex' }, color: '#64748B' }}
          title="ย่อแถบเมนูเพื่อขยายพื้นที่ทำงาน"
        >
          <ChevronLeft size={18} />
        </IconButton>
      </Toolbar>
      <Divider />
      <List component="nav" sx={{ px: 1.5, py: 1.5 }}>
        {navItems.map((item: any) => {
          if (item.children) {
            const isOpen = !!openMenus[item.text];
            const hasActiveChild = item.children.some((child: any) => location.pathname === child.path);
            return (
              <React.Fragment key={item.text}>
                <ListItemButton
                  onClick={() => handleMenuClick(item.text)}
                  sx={{
                    borderRadius: 2.5,
                    mb: 0.5,
                    py: 1,
                    px: 1.5,
                    color: hasActiveChild ? 'primary.main' : '#0F172A',
                    bgcolor: hasActiveChild ? 'rgba(30, 64, 175, 0.04)' : 'transparent',
                    '&:hover': {
                      bgcolor: '#F1F5F9',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: hasActiveChild ? 'primary.main' : '#64748B' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, fontFamily: '"Prompt", sans-serif' }}>
                        {item.text}
                      </Typography>
                    }
                  />
                  {isOpen ? (
                    <ChevronUp size={16} color={hasActiveChild ? '#1E40AF' : '#64748B'} />
                  ) : (
                    <ChevronDown size={16} color={hasActiveChild ? '#1E40AF' : '#64748B'} />
                  )}
                </ListItemButton>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1 }}>
                    {item.children.map((child: any) => {
                      const isSelected = location.pathname === child.path;
                      return (
                        <ListItemButton
                          key={child.text}
                          onClick={() => {
                            navigate(child.path);
                            if (isMobile) setMobileOpen(false);
                          }}
                          selected={isSelected}
                          sx={{
                            borderRadius: 2,
                            mb: 0.4,
                            py: 0.8,
                            pl: 2.5,
                            color: isSelected ? 'primary.main' : '#475569',
                            '&.Mui-selected': {
                              bgcolor: '#EFF6FF',
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: '#DBEAFE',
                              },
                            },
                            '&:hover': {
                              bgcolor: '#F8FAFC',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 28,
                              color: isSelected ? 'primary.main' : '#64748B',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: '0.84rem',
                                  fontWeight: isSelected ? 600 : 500,
                                  fontFamily: '"Prompt", sans-serif',
                                }}
                              >
                                {child.text}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          } else {
            const isSelected =
              location.pathname === item.path ||
              (item.path === '/dashboard' &&
                (location.pathname === '/dashboard' || location.pathname === '/dashboard/'));
            return (
              <ListItemButton
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                selected={isSelected}
                sx={{
                  borderRadius: 2.5,
                  mb: 0.5,
                  py: 1,
                  px: 1.5,
                  color: isSelected ? 'primary.main' : '#0F172A',
                  '&.Mui-selected': {
                    bgcolor: '#EFF6FF',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: '#DBEAFE',
                    },
                  },
                  '&:hover': {
                    bgcolor: '#F1F5F9',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: isSelected ? 'primary.main' : '#64748B' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, fontFamily: '"Prompt", sans-serif' }}>
                      {item.text}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          }
        })}
      </List>

      {/* Upcoming Events in Drawer */}
      <Box sx={{ px: 2, mt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: '#64748B',
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            mb: 1.5,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            fontFamily: '"Prompt", sans-serif',
          }}
        >
          <Calendar size={14} color="#1E40AF" />
          <span>กำหนดการเร็วๆ นี้</span>
        </Typography>
        <List disablePadding>
          {events.slice(0, 4).map((e: any) => (
            <ListItem
              key={e.id}
              disablePadding
              sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Box
                  sx={{
                    width: 3,
                    height: 28,
                    bgcolor: getEventColor(e.event_type),
                    mr: 1.2,
                    borderRadius: 1,
                    mt: 0.4,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      display: 'block',
                      lineHeight: 1.3,
                      color: '#0F172A',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {e.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {new Date(e.event_date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))}
          {events.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>
              ไม่มีกำหนดการเร็วๆ นี้
            </Typography>
          )}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Frosted Glass Sticky AppBar (PTLE Standard) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          background: 'rgba(255, 255, 255, 0.88)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.85)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          color: '#0F172A',
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          {/* Mobile drawer toggle */}
          <IconButton
            color="inherit"
            aria-label="open mobile drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { md: 'none' }, color: '#0F172A' }}
          >
            <MenuIcon size={22} />
          </IconButton>

          {/* Desktop drawer collapse/expand toggle */}
          <IconButton
            color="inherit"
            aria-label="toggle desktop sidebar"
            edge="start"
            onClick={() => setDesktopOpen(!desktopOpen)}
            sx={{ mr: 1.5, display: { xs: 'none', md: 'inline-flex' }, color: '#0F172A' }}
            title={desktopOpen ? 'ย่อแถบเมนูด้านข้าง (ขยายพื้นที่ทำงาน)' : 'ขยายแถบเมนูด้านข้าง'}
          >
            <MenuIcon size={21} />
          </IconButton>

          <Box
            onClick={() => navigate('/dashboard')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              userSelect: 'none',
              gap: 1.5,
            }}
          >
            <Avatar
              src="/logo.png"
              alt="TPMS Logo"
              sx={{
                width: 40,
                height: 40,
                p: 0.4,
                bgcolor: 'primary.main',
                boxShadow: '0 3px 8px rgba(30, 64, 175, 0.22)',
              }}
            />
            <Box sx={{ overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{ fontWeight: 700, color: '#1E3A8A', fontSize: { xs: '0.95rem', sm: '1.1rem' }, lineHeight: 1.2 }}
                >
                  TPMS KPRU
                </Typography>
                <Chip
                  label={getRoleLabel(user.role)}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(30, 64, 175, 0.08)',
                    color: '#1E40AF',
                    display: { xs: 'none', sm: 'inline-flex' },
                  }}
                />
              </Box>
              <Typography variant="caption" noWrap sx={{ color: '#475569', display: 'block', lineHeight: 1.1 }}>
                {profile?.first_name
                  ? `${profile.prefix || ''}${profile.first_name} ${profile.last_name}`
                  : user.username || user.role}
              </Typography>
            </Box>
          </Box>

          {/* Action Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              color="inherit"
              onClick={handleOpenNotifications}
              title="การแจ้งเตือน"
              sx={{ color: '#475569', '&:hover': { color: 'primary.main', bgcolor: '#EFF6FF' } }}
            >
              <Badge badgeContent={notifications.filter((n: any) => !n.is_read).length} color="error">
                <Bell size={20} />
              </Badge>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseNotifications}
              slotProps={{
                paper: {
                  sx: {
                    width: isMobile ? '90vw' : 340,
                    maxHeight: 420,
                    borderRadius: 3,
                    mt: 1,
                    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12)',
                  },
                },
              }}
            >
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  การแจ้งเตือน
                </Typography>
                <Button
                  size="small"
                  onClick={async () => {
                    await api.put('/notifications/read-all');
                    fetchNotifications();
                  }}
                >
                  อ่านทั้งหมด
                </Button>
              </Box>
              <Divider />
              {notifications.length === 0 && <MuiMenuItem disabled>ไม่มีการแจ้งเตือน</MuiMenuItem>}
              {notifications.map((n: any) => (
                <MuiMenuItem
                  key={n.id}
                  onClick={() => handleMarkAsRead(n.id)}
                  sx={{
                    whiteSpace: 'normal',
                    bgcolor: n.is_read ? 'transparent' : '#EFF6FF',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    py: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color={n.is_read ? 'text.secondary' : 'primary.main'}
                    sx={{ fontWeight: n.is_read ? 500 : 700 }}
                  >
                    {n.title}
                  </Typography>
                  <Typography variant="body2">{n.message}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(n.created_at).toLocaleString('th-TH')}
                  </Typography>
                </MuiMenuItem>
              ))}
            </Menu>

            <IconButton
              color="inherit"
              onClick={() => navigate('/')}
              title="หน้าแรกเว็บไซต์ (Landing Page)"
              sx={{ color: '#475569', '&:hover': { color: 'primary.main', bgcolor: '#EFF6FF' } }}
            >
              <Globe size={20} />
            </IconButton>

            <IconButton
              color="inherit"
              onClick={() => navigate('/dashboard/profile')}
              title="ข้อมูลส่วนตัว"
              sx={{ color: '#475569', '&:hover': { color: 'primary.main', bgcolor: '#EFF6FF' } }}
            >
              <User size={20} />
            </IconButton>

            <IconButton
              color="inherit"
              onClick={handleLogout}
              title="ออกจากระบบ"
              sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEF2F2' } }}
            >
              <LogOut size={20} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: desktopOpen ? drawerWidth : 0 },
          flexShrink: { md: 0 },
          transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1)',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="persistent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(226, 232, 240, 0.9)',
            },
          }}
          open={desktopOpen}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content Workspace */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3.5 },
          width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: 70 }} />
        <Container maxWidth={false} sx={{ px: { xs: 0, sm: 1.5 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Routes>
              <Route
                path="/"
                element={
                  user.role === 'RESEARCH_STAFF' ? (
                    <Navigate to="/dashboard/research" replace />
                  ) : (
                    <HomeView profile={profile} />
                  )
                }
              />
              <Route path="/research" element={<ClassroomResearchDatabase />} />
              <Route
                path="/placements"
                element={
                  user.role === 'RESEARCH_STAFF' ? (
                    <Navigate to="/dashboard/research" replace />
                  ) : (
                    <PlacementManager />
                  )
                }
              />
              <Route path="/logs" element={<Navigate to="/dashboard/workload-submission" replace />} />
              <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
              <Route path="/registry-report" element={<RegistryReport />} />
              <Route path="/supervision-report" element={<SupervisionReport />} />
              <Route path="/system-users" element={<SystemUserManager />} />
              <Route path="/supervise" element={<SupervisorStudents />} />
              <Route path="/schools" element={<SchoolManager />} />
              <Route path="/courses" element={<CourseManager />} />
              <Route path="/students" element={<StudentManager />} />
              <Route path="/supervisors" element={<SupervisorManager />} />
              <Route path="/co-teachers" element={<Navigate to="/dashboard/schools" replace />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/announcements" element={<AnnouncementManager />} />
              <Route path="/landing-page" element={<LandingPageManager />} />
              <Route path="/events" element={<EventManager />} />
              <Route path="/resources" element={<ResourceCenter />} />
              <Route path="/school-feedback" element={<SchoolEvaluation />} />
              <Route path="/school-eval-report" element={<SchoolEvaluationReport />} />
              <Route path="/evaluation-forms" element={<EvaluationFormManager />} />
              <Route path="/workload-forms" element={<WorkloadFormManager />} />
              <Route path="/workload-evaluation" element={<SupervisorWorkloadEvaluation />} />
              <Route path="/workload-submission" element={<StudentWorkloadSubmission />} />
              <Route path="/settings" element={<SystemSettings />} />
              <Route path="/audit" element={<AuditLogs />} />
              <Route path="/monitoring" element={<MonitoringDashboard />} />
              <Route path="/recycle-bin" element={<RecycleBin />} />
              <Route path="/evaluations" element={<MyEvaluations />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/portfolio/:placementId" element={<Portfolio />} />
              <Route path="/chat" element={<GuidanceChat />} />
              <Route path="/demand-survey" element={<StudentDemandSurvey />} />
              <Route path="/exams-manage" element={<ComprehensiveExamManager />} />
              <Route path="/exams" element={<StudentExamPortal />} />
              <Route path="/exam-portal" element={<StudentExamPortal />} />
            </Routes>
          </Box>

          {/* Clean Workspace Footer with Simple Icons GitHub Version Badge */}
          <Box component="footer" sx={{ py: 3, mt: 6, textAlign: 'center' }}>
            <MuiDivider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Chip
                icon={<GitHubBrandIcon size={14} />}
                label={`TPMS ${APP_VERSION}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, bgcolor: '#FFFFFF', borderColor: '#CBD5E1' }}
              />
              <Typography variant="body2" color="text.secondary">
                ผู้พัฒนาระบบ วิวัฒน์ ทวีทรัพย์ • ฝ่ายฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Pending Unsaved Workload Evaluation Draft Alert Modal */}
      <WorkloadDraftAlertModal />
    </Box>
  );
};

export default Dashboard;
